/**
 * useSerialScanner — React hook for USB-COM serial barcode scanners.
 *
 * Wraps SerialPortService to provide a reactive interface for components.
 * Features:
 * - Auto-connect to previously granted port on mount
 * - Auto-reconnect on page visibility change
 * - Persistent connection intent via ref
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { SerialPortService, SerialState } from '../services/serialPortService';

interface UseSerialScanner {
  serialState: SerialState;
  isSupported: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

const RECONNECT_DELAY = 1500;

export function useSerialScanner(onBarcode: (barcode: string) => void): UseSerialScanner {
  const [serialState, setSerialState] = useState<SerialState>('disconnected');
  const serviceRef = useRef<SerialPortService | null>(null);
  const onBarcodeRef = useRef(onBarcode);
  /** Tracks whether user wants the scanner connected (persists across reconnects) */
  const shouldBeConnectedRef = useRef(false);

  // Keep callback ref up to date without recreating the service
  useEffect(() => {
    onBarcodeRef.current = onBarcode;
  }, [onBarcode]);

  // Initialize service once
  useEffect(() => {
    const service = new SerialPortService();
    service.onBarcode = (barcode) => onBarcodeRef.current(barcode);
    service.onStateChange = (state) => {
      setSerialState(state);
      // If disconnected unexpectedly while should be connected, try reconnect
      if (state === 'error' && shouldBeConnectedRef.current) {
        console.log('[useSerialScanner] Unexpected disconnect, will auto-reconnect...');
      }
    };
    serviceRef.current = service;

    // Auto-connect to previously granted port (no user gesture needed)
    if (SerialPortService.isSupported()) {
      navigator.serial.getPorts().then(async (ports) => {
        if (ports.length > 0) {
          console.log('[useSerialScanner] Found previously granted port, auto-connecting...');
          try {
            await service.connectToExistingPort(ports[0], 9600);
            shouldBeConnectedRef.current = true;
          } catch (err) {
            console.warn('[useSerialScanner] Auto-connect failed:', err);
          }
        }
      }).catch(() => {});
    }

    return () => {
      shouldBeConnectedRef.current = false;
      service.disconnect().catch(() => {});
      serviceRef.current = null;
    };
  }, []);

  // Auto-reconnect on page visibility change
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!shouldBeConnectedRef.current) return;

      const service = serviceRef.current;
      if (!service) return;

      // If was connected and now in error/disconnected, reconnect
      if (service.state === 'error' || service.state === 'disconnected') {
        console.log('[useSerialScanner] Page visible, reconnecting serial...');
        setTimeout(async () => {
          try {
            // Try previously granted ports first
            const ports = await navigator.serial.getPorts();
            if (ports.length > 0) {
              await service.connectToExistingPort(ports[0], 9600);
            }
          } catch (err) {
            console.warn('[useSerialScanner] Visibility reconnect failed:', err);
          }
        }, RECONNECT_DELAY);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const connect = useCallback(async () => {
    const service = serviceRef.current;
    if (!service) return;

    try {
      await service.requestAndConnect(9600);
      shouldBeConnectedRef.current = true;
    } catch (err) {
      console.warn('[useSerialScanner] Connect failed:', err);
    }
  }, []);

  const disconnect = useCallback(async () => {
    const service = serviceRef.current;
    if (!service) return;
    shouldBeConnectedRef.current = false;
    await service.disconnect();
  }, []);

  return {
    serialState,
    isSupported: SerialPortService.isSupported(),
    connect,
    disconnect,
  };
}
